import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = ["homework", "classwork", "notebook", "project", "participation", "remedial"];
const RATINGS = [
  { v: "excellent", c: "bg-emerald-100 text-emerald-800" },
  { v: "good", c: "bg-sky-100 text-sky-800" },
  { v: "average", c: "bg-amber-100 text-amber-800" },
  { v: "needs_improvement", c: "bg-rose-100 text-rose-800" },
];

export default function Performance() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    student_id: "", date: new Date().toISOString().slice(0, 10),
    category: "homework", rating: "good", note: "",
  });

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/performance"), api.get("/students")]);
    setRows(p.data); setStudents(s.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/performance", form);
      toast.success("Note added");
      setForm({ ...form, note: "" }); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canAdd = ["admin", "principal", "teacher"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">Student Performance</h1>
          <p className="text-zinc-500 mt-1">Homework, classwork, projects, and remedial notes.</p>
        </div>
        {canAdd && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-perf-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" />Add Note</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record Performance</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label>Student</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="perf-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.class_name}-{s.section}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rating</Label>
                    <Select value={form.rating} onValueChange={(v) => setForm({ ...form, rating: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RATINGS.map((r) => <SelectItem key={r.v} value={r.v}>{r.v.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Note</Label><Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
                <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="perf-submit-btn">Save</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Student</TableHead>
              <TableHead>Category</TableHead><TableHead>Rating</TableHead>
              <TableHead>Teacher</TableHead><TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const rc = RATINGS.find((x) => x.v === r.rating) || RATINGS[1];
              return (
                <TableRow key={r.id} className="row-hover">
                  <TableCell className="text-zinc-500 text-sm">{r.date}</TableCell>
                  <TableCell className="font-medium">{r.student_name}</TableCell>
                  <TableCell className="capitalize">{r.category}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wide ${rc.c}`}>{r.rating.replace("_", " ")}</span>
                  </TableCell>
                  <TableCell className="text-zinc-500 text-sm">{r.teacher_name}</TableCell>
                  <TableCell className="text-zinc-600 text-sm">{r.note || "—"}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-500">No performance notes yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
